// client/src/components/influencer/project/modals/ProjectApplyModal.js
import React, { useState, useEffect } from 'react';
import { getSNSAccounts, readSNSCache } from '../../../../utils/snsAccountCache';
import { clearApplyCache } from '../../../../utils/warmApplyCache'; // ✅ 추가

export default function ProjectApplyModal({ open, onClose, project, onSuccess }) {
  const [snsAccounts, setSnsAccounts] = useState({ instagram: [], blog: [], youtube: [], tiktok: [] });
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [initialLoading, setInitialLoading] = useState(false);
  const [hasNoAccounts, setHasNoAccounts] = useState(false);

  // SNS 계정 불러오기 (캐시 우선)
  useEffect(() => {
    if (!open) {
      setInitialLoading(false);
      setHasNoAccounts(false);
      setError('');
      return;
    }
    
    setError('');
    setSelectedAccounts([]);
    setConfirmed(false);
    
    // ✅ 캐시 우선 확인
    const cached = readSNSCache();
    if (cached) {
      // 캐시된 데이터가 있으면 즉시 표시 (로딩 없음)
      setSnsAccounts(cached.groups);
      setHasNoAccounts(cached.total === 0);
      setInitialLoading(false);
      
      // 백그라운드에서 최신 데이터 가져오기 (선택적)
      getSNSAccounts().then(data => {
        setSnsAccounts(data.groups);
        setHasNoAccounts(data.total === 0);
      }).catch(() => {
        // 캐시 데이터가 있으므로 에러 무시
      });
    } else {
      // 캐시가 없으면 로딩 표시하고 가져오기
      setInitialLoading(true);
      
      getSNSAccounts().then(data => {
        console.log('🔍 SNS 계정 데이터:', data); // ✅ 디버깅 로그 추가
        console.log('  - total:', data.total); // ✅ 디버깅 로그 추가
        console.log('  - groups:', data.groups); // ✅ 디버깅 로그 추가
        setSnsAccounts(data.groups);
        setHasNoAccounts(data.total === 0);
      }).catch(err => {
        console.error('SNS 계정 로딩 실패:', err);
        setError('SNS 계정을 불러오는데 실패했습니다.');
      }).finally(() => {
        setInitialLoading(false);
      });
    }
  }, [open]);

  // 계정 선택/해제
  const toggleAccount = (platform, accountName) => {
    const key = `${platform}:${accountName}`;
    setSelectedAccounts(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  // 지원하기
  const handleApply = async () => {
    if (selectedAccounts.length === 0) {
      alert('최소 1개 이상의 SNS 계정을 선택해주세요.');
      return;
    }
    
    if (!confirmed) {
      alert('확인했습니다를 체크해주세요.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/influencer/projects/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          projectId: project?.id,
          snsAccounts: selectedAccounts
        })
      });

      const data = await res.json();

      if (res.ok && data.ok) {
        // ✅ 캐시 무효화 (최신 isApplied 상태 반영)
        try {
          const meRes = await fetch('/api/user/me', { credentials: 'include' });
          const meData = await meRes.json();
          if (meRes.ok && meData.ok && meData.id) {
            clearApplyCache(meData.id);
          }
        } catch {}
        
        onSuccess?.();
      } else {
        // ✅ 수정: 10개 제한 에러 메시지 개선
        const message = data.message || '지원에 실패했습니다.';
        
        // 10개 제한 메시지를 더 명확하게 표시
        if (message.includes('10개') || message.includes('선정 대기')) {
          alert(
            '⚠️ 지원 제한\n\n' +
            '현재 선정 대기 중인 프로젝트가 10개입니다.\n\n' +
            '프로젝트에 선정되거나 모집이 마감될 때까지\n' +
            '새로운 프로젝트에 지원할 수 없습니다.\n\n' +
            '💡 Tip: 마이비즈 > 지원 관리에서\n' +
            '현재 지원 상태를 확인할 수 있습니다.'
          );
        } else {
          alert(message);
        }
      }
    } catch (err) {
      console.error('지원 실패:', err);
      alert('네트워크 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const platforms = [
    { key: 'instagram', icon: '/images/sns/instagram.svg', name: 'Instagram', color: '#E1306C' },
    { key: 'youtube', icon: '/images/sns/youtube.svg', name: 'YouTube', color: '#FF0000' },
    { key: 'tiktok', icon: '/images/sns/tiktok.svg', name: 'TikTok', color: '#000000' },
    { key: 'blog', icon: '/images/sns/blog.svg', name: 'Blog', color: '#00C73C' }
  ];


  // 초기 로딩 중 (캐시 없을 때만)
  if (initialLoading) {
    return (
      <div className="influencer-project-modal" style={{ zIndex: 10001 }}>
        <div className="project-apply-modal__content">
          <div className="project-apply-modal__loading">
            <div className="project-apply-modal__loading-text">
              SNS 계정 정보를 불러오는 중...
            </div>
            <div className="project-apply-modal__loading-spinner-wrap">
              <div className="project-apply-modal__spinner" />
            </div>
          </div>
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // SNS 계정이 없는 경우 - 전용 모달
  if (hasNoAccounts) {
    return (
      <div className="influencer-project-modal" style={{ zIndex: 10001 }}>
        <div className="project-apply-modal__content project-apply-modal__content--narrow">
          <button
            onClick={onClose}
            aria-label="close"
            className="influencer-project-modal__close"
          >
            ×
          </button>

          <div className="project-apply-modal__empty">
            <div className="project-apply-modal__empty-icon">😢</div>
            <h2 className="project-apply-modal__empty-title">
              등록된 SNS 계정이 없습니다
            </h2>
            <div className="project-apply-modal__empty-text">
              프로젝트에 지원하려면<br />
              먼저 SNS 계정을 등록해주세요.
            </div>
            <button
              onClick={onClose}
              className="project-apply-modal__empty-btn"
            >
              확인
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 네트워크 에러 등 다른 에러
  if (error && !hasNoAccounts) {
    return (
      <div className="influencer-project-modal" style={{ zIndex: 10001 }}>
        <div className="project-apply-modal__content project-apply-modal__content--narrow">
          <button
            onClick={onClose}
            aria-label="close"
            className="influencer-project-modal__close"
          >
            ×
          </button>

          <div className="project-apply-modal__empty">
            <div className="project-apply-modal__empty-icon">⚠️</div>
            <h2 className="project-apply-modal__empty-title project-apply-modal__empty-title--error">
              오류 발생
            </h2>
            <div className="project-apply-modal__empty-text">
              {error}
            </div>
            <button
              onClick={onClose}
              className="project-apply-modal__empty-btn project-apply-modal__empty-btn--gray"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 정상 모달 (SNS 계정 있음)
  return (
    <div className="influencer-project-modal" style={{ zIndex: 10001 }}>
      <div className="project-apply-modal__content">
        {/* 고정 헤더 바 */}
        <div className="project-apply-modal__header-bar">
          <h2 className="project-apply-modal__title">
            프로젝트 지원
          </h2>
          <button
            onClick={onClose}
            aria-label="close"
            className="project-apply-modal__close"
          >
          </button>
        </div>

        {/* 스크롤 가능한 본문 */}
        <div className="project-apply-modal__body">

        <div className="project-apply-modal__info-box">
          <div className="project-apply-modal__info-icon">💡</div>
          <div className="project-apply-modal__info-content">
            <div className="project-apply-modal__info-main">
              프로젝트에 지원할 SNS 계정을 선택해주세요
            </div>
            <div className="project-apply-modal__info-sub">
              선정 후 계약 시, 선택한 모든 계정에 콘텐츠를 업로드해야 합니다
            </div>
          </div>
        </div>

        {(() => {
          // 광고주가 모집하는 플랫폼만 필터링
          const projectPlatforms = project?.platforms || [];
          const filteredPlatforms = platforms.filter(p => projectPlatforms.includes(p.key));
          
          // 모집하는 플랫폼 중 인플루언서가 가진 계정이 있는지 확인
          const hasMatchingAccounts = filteredPlatforms.some(p => {
            const accounts = snsAccounts[p.key] || [];
            return accounts.length > 0;
          });

          // 매칭되는 계정이 없는 경우
          if (!hasMatchingAccounts) {
            return (
              <div className="project-apply-modal__no-match">
                <div className="project-apply-modal__no-match-icon">🚫</div>
                <div className="project-apply-modal__no-match-title">
                  지원 가능한 SNS 계정이 없습니다
                </div>
                <div className="project-apply-modal__no-match-text">
                  이 프로젝트는 <strong>{filteredPlatforms.map(p => p.name).join(', ')}</strong> 계정을 모집합니다.<br />
                  해당 플랫폼의 계정을 등록한 후 지원해주세요.
                </div>
              </div>
            );
          }

          // 매칭되는 계정이 있는 경우 - 필터링된 플랫폼만 표시
          return (
            <div className="project-apply-modal__accounts-section">
              {filteredPlatforms.map(({ key, icon, name, color }) => {
                const accounts = snsAccounts[key] || [];
                if (accounts.length === 0) return null;

                return (
                  <div key={key} className="project-apply-modal__platform-group">
                    {accounts.map((acc, idx) => {
                      const accountKey = `${key}:${acc.accountName}`;
                      const isSelected = selectedAccounts.includes(accountKey);

                      return (
                        <div
                          key={idx}
                          onClick={() => toggleAccount(key, acc.accountName)}
                          className={`project-apply-modal__account ${isSelected ? 'project-apply-modal__account--selected' : ''}`}
                          style={{
                            borderColor: isSelected ? color : '#e5e7eb',
                            '--platform-color': color
                          }}
                        >
                          <div className="project-apply-modal__account-icon-wrapper">
                            <img src={icon} alt={name} className="project-apply-modal__account-icon" />
                          </div>
                          <div className="project-apply-modal__account-info">
                            <div className="project-apply-modal__account-platform">{name}</div>
                            <div className="project-apply-modal__account-name">
                              @{acc.accountName}
                            </div>
                            <div className="project-apply-modal__account-followers">
                              {Number(acc.followerCount || 0).toLocaleString()} 팔로워
                            </div>
                          </div>
                          <div className="project-apply-modal__account-check">
                            {isSelected ? (
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" fill={color} />
                                <path d="M8 12l3 3 5-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            ) : (
                              <div className="project-apply-modal__account-check-empty"></div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })()}

        <div className="project-apply-modal__divider"></div>

        <div className="project-apply-modal__process-section">
          <div className="project-apply-modal__process-title">📝 진행 절차</div>
          <div className="project-apply-modal__process-steps">
            <div className="project-apply-modal__process-step">
              <span className="project-apply-modal__process-number">1</span>
              <span>지원</span>
            </div>
            <div className="project-apply-modal__process-arrow">→</div>
            <div className="project-apply-modal__process-step">
              <span className="project-apply-modal__process-number">2</span>
              <span>광고주 선정</span>
            </div>
            <div className="project-apply-modal__process-arrow">→</div>
            <div className="project-apply-modal__process-step">
              <span className="project-apply-modal__process-number">3</span>
              <span>계약 수락</span>
            </div>
          </div>
        </div>

        <label className="project-apply-modal__confirm-label">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="project-apply-modal__confirm-checkbox"
          />
          <span className="project-apply-modal__confirm-text">위 내용을 확인했으며 동의합니다</span>
        </label>

        <button
          onClick={handleApply}
          disabled={loading || selectedAccounts.length === 0 || !confirmed}
          className={`project-apply-modal__submit-btn ${(loading || selectedAccounts.length === 0 || !confirmed) ? 'project-apply-modal__submit-btn--disabled' : ''}`}
        >
          {loading ? '지원 중...' : `지원하기${selectedAccounts.length > 0 ? ` (${selectedAccounts.length}개 계정)` : ''}`}
        </button>
        </div>
      </div>
    </div>
  );
}